import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  takeWhile,
  withLatestFrom,
} from 'rxjs/operators';
import { extractErrorMessage } from '@core/http/api-response.envelope';
import * as TransactionActions from './transaction.actions';
import * as TransactionSelectors from './transaction.selectors';
import { TransactionService } from '../services/transaction.service';
import {
  TransactionOrderBy,
  TransactionDetailFailure,
  TransactionProjectionStatusEnum,
} from '../models/transaction.model';
import * as VerificationActions from '@shared/store/verification/verification.actions';
import { selectVerificationActionId } from '@shared/store/verification/verification.selectors';

/** List-table column keys → API `orderBy` query values */
function mapSortActiveToOrderBy(sortActive: string): TransactionOrderBy {
  switch (sortActive) {
    case 'requestTime':
    case 'date':
      return 'createdAt';
    case 'status':
      return 'status';
    case 'asset':
      return 'assetName';
    case 'amount':
      return 'amount';
    case 'destination':
      return 'dst';
    case 'origin':
      return 'src';
    case 'requester':
      return 'createdBy';
    case 'fee':
      return 'fee';
    default:
      return 'createdAt';
  }
}

@Injectable()
export class TransactionEffects {
  private readonly translate = inject(TranslateService);

  loadTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadTransactions),
      withLatestFrom(
        this.store.select(TransactionSelectors.selectActiveTab),
        this.store.select(TransactionSelectors.selectPageIndex),
        this.store.select(TransactionSelectors.selectPageSize),
        this.store.select(TransactionSelectors.selectCurrentFilters),
        this.store.select(TransactionSelectors.selectSearchTerm),
        this.store.select(TransactionSelectors.selectSortActive),
        this.store.select(TransactionSelectors.selectSortDirection)
      ),
      switchMap(
        ([
          action,
          activeTab,
          pageIndex,
          pageSize,
          filters,
          searchTerm,
          sortActive,
          sortDirection,
        ]) => {
          const type = action.listType ?? activeTab;
          const page = pageIndex + 1;
          const orderBy = mapSortActiveToOrderBy(sortActive);
          const order = sortDirection;

          return this.transactionService
            .getTransactions(
              type,
              page,
              pageSize,
              filters,
              searchTerm || undefined,
              orderBy,
              order
            )
            .pipe(
              map(res =>
                TransactionActions.loadTransactionsSuccess({
                  data: res.data ?? [],
                  total: res.pagination?.total ?? 0,
                })
              ),
              catchError(() =>
                of(
                  TransactionActions.loadTransactionsFailure({
                    error: this.translate.instant(
                      'transactions.errors.loadList'
                    ),
                  })
                )
              )
            );
        }
      )
    )
  );

  reloadOnTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.setActiveTab),
      map(({ tab }) => TransactionActions.loadTransactions({ listType: tab }))
    )
  );

  reloadOnPaginationChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.setPagination),
      map(() => TransactionActions.loadTransactions({}))
    )
  );

  reloadOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TransactionActions.setFilters,
        TransactionActions.clearFilters,
        TransactionActions.removeFilterChip
      ),
      map(() => TransactionActions.loadTransactions({}))
    )
  );

  reloadOnSearchChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.setSearch),
      map(() => TransactionActions.loadTransactions({}))
    )
  );

  reloadOnSortChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.setSort),
      map(() => TransactionActions.loadTransactions({}))
    )
  );

  loadTransactionDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadTransactionDetail),
      switchMap(({ transactionId }) =>
        this.transactionService.getTransactionById(transactionId).pipe(
          map(response =>
            TransactionActions.loadTransactionDetailSuccess({ response })
          ),
          catchError(error =>
            of(
              TransactionActions.loadTransactionDetailFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant('transactions.errors.loadDetail'),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Silent re-fetch of GET /approval-requests/external/:id/progress.
   * Driven by the realtime dispatcher when an approval envelope
   * (`APPROVAL_REQUESTED` / `_APPROVED` / `_REJECTED` / `_CANCELED` /
   * `_STAGE_COMPLETED` / `_REQUEST_EXPIRED`) arrives whose `requestId`
   * matches the open detail's `approvalRequestId`. Replaces the old
   * timer-based `pollApprovalProgressWhileStep02Selected$` — same
   * response shape, same `refreshApprovalProgressByExternalIdSuccess`
   * reducer path, no `approvalProgressLoading: true` flicker.
   */
  refreshApprovalProgressByExternalId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.refreshApprovalProgressByExternalId),
      switchMap(({ externalRequestId }) =>
        this.transactionService
          .getApprovalRequestProgressByExternalId(externalRequestId)
          .pipe(
            map(res =>
              TransactionActions.refreshApprovalProgressByExternalIdSuccess({
                data: res.data,
              })
            ),
            catchError(() => EMPTY)
          )
      )
    )
  );

  /**
   * Load approval requirements only when step-02 is selected.
   * This prevents calling policy-management on refresh when user isn't on step 2.
   */
  loadApprovalProgressWhenStep02Selected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TransactionActions.setDetailSelectedStep,
        TransactionActions.loadTransactionDetailSuccess,
        TransactionActions.applyDetailStatusFromEvent
      ),
      withLatestFrom(
        this.store.select(TransactionSelectors.selectDetailSelectedStep),
        this.store.select(TransactionSelectors.selectDetailApprovalRequestId),
        this.store.select(TransactionSelectors.selectApprovalProgressLoading),
        this.store.select(TransactionSelectors.selectApprovalProgress)
      ),
      filter(
        ([, selectedStep, approvalRequestId, loading, progress]) =>
          selectedStep === 2 &&
          Boolean(String(approvalRequestId ?? '').trim()) &&
          !loading &&
          !progress
      ),
      map(([, , approvalRequestId]) =>
        TransactionActions.loadApprovalProgressByExternalId({
          externalRequestId: String(approvalRequestId),
        })
      )
    )
  );

  loadApprovalProgressByExternalId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadApprovalProgressByExternalId),
      switchMap(({ externalRequestId }) =>
        this.transactionService
          .getApprovalRequestProgressByExternalId(externalRequestId)
          .pipe(
            map(res =>
              TransactionActions.loadApprovalProgressByExternalIdSuccess({
                data: res.data,
              })
            ),
            catchError(error =>
              of(
                TransactionActions.loadApprovalProgressByExternalIdFailure({
                  error:
                    error?.error?.message ||
                    error?.message ||
                    this.translate.instant('transactions.errors.loadApproval'),
                })
              )
            )
          )
      )
    )
  );

  loadTransactionStep01Details$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadTransactionStep01Details),
      switchMap(({ transactionId }) =>
        this.transactionService.getTransactionStep01Details(transactionId).pipe(
          map(res =>
            TransactionActions.loadTransactionStep01DetailsSuccess({
              data: res.data,
            })
          ),
          catchError(error =>
            of(
              TransactionActions.loadTransactionStep01DetailsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant('transactions.errors.loadStep01'),
              })
            )
          )
        )
      )
    )
  );

  loadSigningChannels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadSigningChannels),
      switchMap(({ transactionId }) =>
        this.transactionService
          .getSigningNotificationChannels(transactionId)
          .pipe(
            map(response =>
              TransactionActions.loadSigningChannelsSuccess({ response })
            ),
            catchError(error =>
              of(
                TransactionActions.loadSigningChannelsFailure({
                  error:
                    error?.error?.message ||
                    error?.message ||
                    this.translate.instant('transactions.errors.loadSigning'),
                })
              )
            )
          )
      )
    )
  );

  loadNetworkDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadNetworkDetails),
      switchMap(({ transactionId }) =>
        this.transactionService
          .getTransactionNetworkDetails(transactionId)
          .pipe(
            map(response =>
              TransactionActions.loadNetworkDetailsSuccess({ response })
            ),
            catchError(error =>
              of(
                TransactionActions.loadNetworkDetailsFailure({
                  error:
                    error?.error?.message ||
                    error?.message ||
                    this.translate.instant('transactions.errors.loadNetwork'),
                })
              )
            )
          )
      )
    )
  );

  /**
   * Kick off broadcast. On 202 we publish the challenge to the shared
   * verification slice (source: 'broadcast') and only clear the local
   * `broadcastSubmitting` flag. On 200 we go straight to success +
   * load-network-details.
   */
  broadcastTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.broadcastTransaction),
      switchMap(({ transactionId }) =>
        this.transactionService.broadcastTransaction(transactionId).pipe(
          mergeMap(result => {
            if (result.type === 'verification_required') {
              return of(
                VerificationActions.setVerificationChallenge({
                  actionId: result.data.actionId,
                  method: result.data.verificationMethod,
                  expiresAt: result.data.expiresAt,
                  source: 'broadcast',
                }),
                TransactionActions.broadcastTransactionVerificationRequired({
                  transactionId,
                })
              );
            }
            return of(
              TransactionActions.broadcastTransactionSuccess({
                response: result.response,
              }),
              TransactionActions.loadNetworkDetails({ transactionId })
            );
          }),
          catchError((err: unknown) =>
            of(
              TransactionActions.broadcastTransactionFailure({
                transactionId,
                failure: this.toBroadcastFailure(err),
              })
            )
          )
        )
      )
    )
  );

  /**
   * When the shared verification slice signals a verified code originating
   * from this feature (`source === 'broadcast'`), retry POST /broadcast
   * with the issued `actionId` to actually broadcast on-chain — but only
   * while that handle is still the live challenge (F-053, F-055): this
   * effect outlives the screen, so it must not act on an announcement about
   * a challenge the operator has left.
   */
  confirmBroadcastAfterVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerificationActions.submitVerificationCodeSuccess),
      filter(({ source }) => source === 'broadcast'),
      withLatestFrom(
        this.store.select(TransactionSelectors.selectTransactionDetail),
        this.store.select(selectVerificationActionId)
      ),
      filter(
        ([{ actionId }, detail, liveActionId]) =>
          actionId === liveActionId && Boolean(detail?.data?.id)
      ),
      map(([{ actionId }, detail]) =>
        TransactionActions.confirmBroadcast({
          transactionId: String(detail!.data.id),
          actionId,
        })
      )
    )
  );

  confirmBroadcast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.confirmBroadcast),
      switchMap(({ transactionId, actionId }) =>
        this.transactionService.confirmBroadcast(transactionId, actionId).pipe(
          mergeMap(response =>
            of(
              VerificationActions.clearVerificationChallenge(),
              TransactionActions.broadcastTransactionSuccess({ response }),
              TransactionActions.loadNetworkDetails({ transactionId })
            )
          ),
          catchError((err: unknown) =>
            of(
              VerificationActions.clearVerificationChallenge(),
              TransactionActions.broadcastTransactionFailure({
                transactionId,
                failure: this.toBroadcastFailure(err),
              })
            )
          )
        )
      )
    )
  );

  private toBroadcastFailure(err: unknown): TransactionDetailFailure {
    const anyErr = err as any;
    const payload = anyErr?.error ?? anyErr ?? {};
    return {
      code:
        String(
          payload?.errorCode ??
            payload?.code ??
            payload?.broadcastFailure?.code ??
            'BROADCAST_UNKNOWN'
        ) || 'BROADCAST_UNKNOWN',
      reason:
        String(
          payload?.message ??
            payload?.reason ??
            payload?.broadcastFailure?.reason ??
            this.translate.instant('transactions.errors.broadcastFailed')
        ) || this.translate.instant('transactions.errors.broadcastFailed'),
    };
  }

  /* ---------------------------------------------------------------------- */
  /* User-selected fee at execution (Part D — step 3 "Choose fee & Execute") */
  /* ---------------------------------------------------------------------- */

  /** Light poll cadence for fee-options while sitting on step 3 (fees drift). */
  private readonly FEE_OPTIONS_POLL_INTERVAL_MS = 15000;

  /** GET /transaction/:id/fee-options (explicit load + each poll tick). */
  loadFeeOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadFeeOptions),
      switchMap(({ transactionId }) =>
        this.transactionService.getFeeOptions(transactionId).pipe(
          map(res =>
            TransactionActions.loadFeeOptionsSuccess({ data: res.data })
          ),
          catchError(error =>
            of(
              TransactionActions.loadFeeOptionsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant('transactions.errors.loadFeeOptions'),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Light auto-poll of fee-options while step 3 is selected and the projection is
   * AWAITING_EXECUTION (Q7). The initial fetch is dispatched by the step-03
   * component; this re-dispatches `loadFeeOptions` on an interval and stops as soon
   * as the projection leaves AWAITING_EXECUTION (e.g. after Execute → SIGNING) or
   * the detail is cleared/reloaded.
   */
  pollFeeOptionsWhileAwaitingExecution$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TransactionActions.loadTransactionDetailSuccess,
        TransactionActions.setDetailSelectedStep
      ),
      withLatestFrom(
        this.store.select(TransactionSelectors.selectDetailSelectedStep),
        this.store.select(TransactionSelectors.selectTransactionDetail)
      ),
      switchMap(([, selectedStep, detail]) => {
        if (selectedStep !== 3) return EMPTY;
        const transactionId = String(detail?.data?.id ?? '').trim();
        if (!transactionId) return EMPTY;
        const stop$ = this.actions$.pipe(
          ofType(
            TransactionActions.clearTransactionDetail,
            TransactionActions.loadTransactionDetail
          )
        );
        return timer(
          this.FEE_OPTIONS_POLL_INTERVAL_MS,
          this.FEE_OPTIONS_POLL_INTERVAL_MS
        ).pipe(
          takeUntil(stop$),
          withLatestFrom(
            this.store.select(TransactionSelectors.selectDetailProjectionState)
          ),
          takeWhile(
            ([, state]) =>
              state === TransactionProjectionStatusEnum.AWAITING_EXECUTION,
            false
          ),
          map(() => TransactionActions.loadFeeOptions({ transactionId }))
        );
      })
    )
  );

  /**
   * Kick off Execute. On 202 publish the challenge to the shared verification
   * slice (source: 'execute') and clear the local `executeSubmitting` flag; the
   * chosen `feeChoice` is retained in `pendingExecuteFeeChoice`. On 200 go straight
   * to success + reload signing channels so the step-3 spinner reflects SIGNING.
   */
  executeTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.executeTransaction),
      switchMap(({ transactionId, feeChoice }) =>
        this.transactionService
          .executeTransaction(transactionId, feeChoice)
          .pipe(
            mergeMap(result => {
              if (result.type === 'verification_required') {
                return of(
                  VerificationActions.setVerificationChallenge({
                    actionId: result.data.actionId,
                    method: result.data.verificationMethod,
                    expiresAt: result.data.expiresAt,
                    source: 'execute',
                  }),
                  TransactionActions.executeTransactionVerificationRequired({
                    transactionId,
                  })
                );
              }
              return of(
                TransactionActions.executeTransactionSuccess({
                  response: result.response,
                }),
                TransactionActions.loadSigningChannels({ transactionId })
              );
            }),
            catchError((err: unknown) =>
              of(
                TransactionActions.executeTransactionFailure({
                  transactionId,
                  failure: this.toExecuteFailure(err),
                })
              )
            )
          )
      )
    )
  );

  /**
   * When the shared verification slice signals a verified code originating from
   * this feature (`source === 'execute'`), retry POST /execute with the issued
   * `actionId` and the retained `feeChoice` to kick off signing — only while
   * that handle is still the live challenge (F-053, F-055).
   */
  confirmExecuteAfterVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerificationActions.submitVerificationCodeSuccess),
      filter(({ source }) => source === 'execute'),
      withLatestFrom(
        this.store.select(TransactionSelectors.selectTransactionDetail),
        this.store.select(TransactionSelectors.selectPendingExecuteFeeChoice),
        this.store.select(selectVerificationActionId)
      ),
      filter(
        ([{ actionId }, detail, feeChoice, liveActionId]) =>
          actionId === liveActionId &&
          Boolean(detail?.data?.id) &&
          Boolean(feeChoice)
      ),
      map(([{ actionId }, detail, feeChoice]) =>
        TransactionActions.confirmExecute({
          transactionId: String(detail!.data.id),
          actionId,
          feeChoice: feeChoice!,
        })
      )
    )
  );

  confirmExecute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.confirmExecute),
      switchMap(({ transactionId, actionId, feeChoice }) =>
        this.transactionService
          .confirmExecute(transactionId, actionId, feeChoice)
          .pipe(
            mergeMap(response =>
              of(
                VerificationActions.clearVerificationChallenge(),
                TransactionActions.executeTransactionSuccess({ response }),
                TransactionActions.loadSigningChannels({ transactionId })
              )
            ),
            catchError((err: unknown) =>
              of(
                VerificationActions.clearVerificationChallenge(),
                TransactionActions.executeTransactionFailure({
                  transactionId,
                  failure: this.toExecuteFailure(err),
                })
              )
            )
          )
      )
    )
  );

  /**
   * Kick off Cancel (OWNER only). On 202 publish the challenge to the shared
   * verification slice (source: 'cancel-transaction'); the reason is kept in
   * `pendingCancelReason` for the verified retry. On 200 the detail is final.
   */
  cancelTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.cancelTransaction),
      switchMap(({ transactionId, reason }) =>
        this.transactionService.cancelTransaction(transactionId, reason).pipe(
          mergeMap(result =>
            result.type === 'verification_required'
              ? of(
                  VerificationActions.setVerificationChallenge({
                    actionId: result.data.actionId,
                    method: result.data.verificationMethod,
                    expiresAt: result.data.expiresAt,
                    source: TransactionActions.CANCEL_TRANSACTION_SOURCE,
                  }),
                  TransactionActions.cancelTransactionVerificationRequired({
                    transactionId,
                  })
                )
              : of(
                  TransactionActions.cancelTransactionSuccess({
                    response: result.response,
                  }),
                  TransactionActions.loadSigningChannels({ transactionId })
                )
          ),
          catchError(() =>
            of(TransactionActions.cancelTransactionFailure({ transactionId }))
          )
        )
      )
    )
  );

  /** A verified code for this feature's live cancel challenge → the verified retry. */
  confirmCancelAfterVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerificationActions.submitVerificationCodeSuccess),
      filter(
        ({ source }) => source === TransactionActions.CANCEL_TRANSACTION_SOURCE
      ),
      withLatestFrom(
        this.store.select(TransactionSelectors.selectTransactionDetail),
        this.store.select(TransactionSelectors.selectPendingCancelReason),
        this.store.select(selectVerificationActionId)
      ),
      filter(
        ([{ actionId }, detail, , liveActionId]) =>
          actionId === liveActionId && Boolean(detail?.data?.id)
      ),
      map(([{ actionId }, detail, reason]) =>
        TransactionActions.confirmCancel({
          transactionId: String(detail!.data.id),
          actionId,
          reason,
        })
      )
    )
  );

  confirmCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.confirmCancel),
      switchMap(({ transactionId, actionId, reason }) =>
        this.transactionService
          .confirmCancel(transactionId, actionId, reason)
          .pipe(
            mergeMap(response =>
              of(
                VerificationActions.clearVerificationChallenge(),
                TransactionActions.cancelTransactionSuccess({ response }),
                TransactionActions.loadSigningChannels({ transactionId })
              )
            ),
            catchError(() =>
              of(
                VerificationActions.clearVerificationChallenge(),
                TransactionActions.cancelTransactionFailure({ transactionId })
              )
            )
          )
      )
    )
  );

  private toExecuteFailure(err: unknown): TransactionDetailFailure {
    const anyErr = err as any;
    const payload = anyErr?.error ?? anyErr ?? {};
    return {
      code:
        String(payload?.errorCode ?? payload?.code ?? 'EXECUTE_UNKNOWN') ||
        'EXECUTE_UNKNOWN',
      reason:
        String(
          payload?.message ??
            payload?.reason ??
            this.translate.instant('transactions.errors.executeFailed')
        ) || this.translate.instant('transactions.errors.executeFailed'),
    };
  }

  /** When detail projection is CONFIRMING, load on-chain confirmation progress */
  requestBlockchainDetailsWhenStep04Selected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TransactionActions.loadTransactionDetailSuccess,
        TransactionActions.applyDetailStatusFromEvent,
        TransactionActions.broadcastTransactionSuccess,
        TransactionActions.setDetailSelectedStep
      ),
      withLatestFrom(
        this.store.select(TransactionSelectors.selectDetailSelectedStep),
        this.store.select(TransactionSelectors.selectTransactionDetail),
        this.store.select(TransactionSelectors.selectBlockchainDetailsLoading),
        this.store.select(TransactionSelectors.selectBlockchainDetails)
      ),
      filter(([, selectedStep, detail, loading, blockchainDetails]) => {
        if (selectedStep !== 4) return false;
        const transactionId = String(detail?.data?.id ?? '').trim();
        if (!transactionId) return false;
        if (loading || blockchainDetails) return false;
        return true;
      }),
      map(([, , detail]) =>
        TransactionActions.loadBlockchainDetails({
          transactionId: String(detail!.data.id),
        })
      )
    )
  );

  loadBlockchainDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadBlockchainDetails),
      switchMap(({ transactionId }) =>
        this.transactionService
          .getTransactionBlockchainDetails(transactionId)
          .pipe(
            map(response =>
              TransactionActions.loadBlockchainDetailsSuccess({ response })
            ),
            catchError(error =>
              of(
                TransactionActions.loadBlockchainDetailsFailure({
                  error:
                    error?.error?.message ||
                    error?.message ||
                    this.translate.instant(
                      'transactions.errors.loadBlockchain'
                    ),
                })
              )
            )
          )
      )
    )
  );

  loadCreateFlowNetworkFee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadCreateFlowNetworkFee),
      switchMap(({ walletId }) =>
        this.transactionService.getNetworkFee(walletId).pipe(
          map(res =>
            TransactionActions.loadCreateFlowNetworkFeeSuccess({
              data: res.data,
            })
          ),
          catchError(error =>
            of(
              TransactionActions.loadCreateFlowNetworkFeeFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant('transactions.errors.networkFee'),
              })
            )
          )
        )
      )
    )
  );

  loadCreateFlowWithdrawalLimitSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadCreateFlowWithdrawalLimitSummary),
      switchMap(({ walletId }) =>
        this.transactionService.getWithdrawalLimitSummary(walletId).pipe(
          map(res =>
            TransactionActions.loadCreateFlowWithdrawalLimitSummarySuccess({
              data: res.data,
            })
          ),
          catchError(error =>
            of(
              TransactionActions.loadCreateFlowWithdrawalLimitSummaryFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant(
                    'transactions.errors.withdrawalSummary'
                  ),
              })
            )
          )
        )
      )
    )
  );

  loadDetailWithdrawalLimitSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadDetailWithdrawalLimitSummary),
      switchMap(({ walletId }) =>
        this.transactionService.getWithdrawalLimitSummary(walletId).pipe(
          map(res =>
            TransactionActions.loadDetailWithdrawalLimitSummarySuccess({
              walletId,
              data: res.data,
            })
          ),
          catchError((error: unknown) =>
            of(
              TransactionActions.loadDetailWithdrawalLimitSummaryFailure({
                walletId,
                error: extractErrorMessage(
                  error,
                  this.translate.instant(
                    'transactions.errors.withdrawalSummary'
                  )
                ),
              })
            )
          )
        )
      )
    )
  );

  loadCreateFlowPricingRate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.loadCreateFlowPricingRate),
      switchMap(({ baseSymbol, quoteSymbol }) => {
        const base = String(baseSymbol ?? '').trim();
        const quote = String(quoteSymbol ?? 'USDT').trim() || 'USDT';
        return this.transactionService.getPricingRates([base], quote).pipe(
          map(res => {
            const item = (res?.data ?? []).find(
              x =>
                String(x?.baseSymbol ?? '').toUpperCase() ===
                  base.toUpperCase() &&
                String(x?.quoteSymbol ?? '').toUpperCase() ===
                  quote.toUpperCase()
            );
            const rate = item?.success ? Number(item.rate) : NaN;
            if (!Number.isFinite(rate) || rate <= 0) {
              return TransactionActions.loadCreateFlowPricingRateFailure({
                baseSymbol: base,
                quoteSymbol: quote,
                error:
                  item?.error ??
                  this.translate.instant('transactions.errors.pricingRate'),
              });
            }
            return TransactionActions.loadCreateFlowPricingRateSuccess({
              baseSymbol: base,
              quoteSymbol: quote,
              rate,
            });
          }),
          catchError(error =>
            of(
              TransactionActions.loadCreateFlowPricingRateFailure({
                baseSymbol: base,
                quoteSymbol: quote,
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant('transactions.errors.pricingRate'),
              })
            )
          )
        );
      })
    )
  );

  createWithdrawal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.createWithdrawal),
      switchMap(({ walletId, body }) =>
        this.transactionService.createWithdrawal(walletId, body).pipe(
          map(response =>
            TransactionActions.createWithdrawalSuccess({
              response: response.body,
              status: response.status,
            })
          ),
          catchError(error =>
            of(
              TransactionActions.createWithdrawalFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant(
                    'transactions.errors.createWithdrawal'
                  ),
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private transactionService: TransactionService
  ) {}
}
